import { useEffect, useRef, useState } from 'react'
import { createChart, CandlestickSeries, HistogramSeries, LineSeries, LineStyle } from 'lightweight-charts'
import type { UTCTimestamp } from 'lightweight-charts'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { generateMarketReport } from '../../api/market'
import { fetchProfile, sendMarketReport } from '../../api/subscription'
import type { DayPrediction, MarketAnalysis, MarketReportResponse, NewsItem, Prediction7d } from '../../types/market'
import type { UserProfile } from '../../types/subscription'
import { Spinner } from '../common/Spinner'
import { usePendingReports } from '../../context/PendingReportsContext'
import { useAuth } from '../../auth/AuthContext'

// ─── constants ───────────────────────────────────────────────────────────────

const ASSETS = ['SOL', 'BTC', 'ETH'] as const
type Asset = typeof ASSETS[number]

// ─── helpers ─────────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1)  return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

/** Wilder's smoothing RSI from candle close prices. */
function calcRsi(candles: MarketAnalysis['candles'], period = 14) {
  const result: Array<{ time: UTCTimestamp; value: number }> = []
  if (candles.length < period + 1) return result

  let avgGain = 0, avgLoss = 0
  for (let i = 1; i <= period; i++) {
    const d = candles[i].close - candles[i - 1].close
    if (d > 0) avgGain += d; else avgLoss -= d
  }
  avgGain /= period; avgLoss /= period

  for (let i = period; i < candles.length; i++) {
    if (i > period) {
      const d = candles[i].close - candles[i - 1].close
      avgGain = (avgGain * (period - 1) + Math.max(d, 0)) / period
      avgLoss = (avgLoss * (period - 1) + Math.max(-d, 0)) / period
    }
    const rsi = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss)
    result.push({ time: candles[i].timestamp as UTCTimestamp, value: rsi })
  }
  return result
}

// ─── candlestick chart with volume + RSI panes ───────────────────────────────

function CandlestickChart({ analysis, prediction7d }: { analysis: MarketAnalysis; prediction7d?: Prediction7d }) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = containerRef.current
    if (!el || !analysis.candles.length) return

    const chart = createChart(el, {
      width: el.clientWidth,
      height: 480,
      layout: { background: { color: '#0F1424' }, textColor: '#8B8FA3' },
      grid: { vertLines: { color: "#1F2747" }, horzLines: { color: "#1F2747" } },
      rightPriceScale: { borderColor: "#1F2747" },
      timeScale: { borderColor: "#1F2747", timeVisible: true },
      crosshair: { vertLine: { color: '#4b5563' }, horzLine: { color: '#4b5563' } },
    })

    // ── Pane 0: Candlesticks ──────────────────────────────────────────────────
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#22c55e', downColor: '#ef4444',
      borderVisible: false,
      wickUpColor: '#22c55e', wickDownColor: '#ef4444',
    }, 0)

    candleSeries.setData(
      analysis.candles.map(c => ({
        time:  c.timestamp as UTCTimestamp,
        open:  c.open, high: c.high, low: c.low, close: c.close,
      }))
    )

    candleSeries.createPriceLine({
      price: analysis.nearestSupport, color: '#22c55e',
      lineWidth: 1, lineStyle: LineStyle.Dashed, title: 'S', axisLabelVisible: true,
    })
    candleSeries.createPriceLine({
      price: analysis.nearestResistance, color: '#ef4444',
      lineWidth: 1, lineStyle: LineStyle.Dashed, title: 'R', axisLabelVisible: true,
    })
    candleSeries.createPriceLine({
      price: analysis.emaFast, color: '#60a5fa',
      lineWidth: 1, lineStyle: LineStyle.SparseDotted, title: 'EMA F', axisLabelVisible: false,
    })
    candleSeries.createPriceLine({
      price: analysis.emaSlow, color: '#f59e0b',
      lineWidth: 1, lineStyle: LineStyle.SparseDotted, title: 'EMA S', axisLabelVisible: false,
    })

    // ── Pane 1: Volume ────────────────────────────────────────────────────────
    const volSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'vol',
    }, 1)

    volSeries.setData(
      analysis.candles.map(c => ({
        time:  c.timestamp as UTCTimestamp,
        value: c.volume,
        color: c.close >= c.open ? '#22c55e44' : '#ef444444',
      }))
    )

    // ── Pane 2: RSI ───────────────────────────────────────────────────────────
    const rsiData = calcRsi(analysis.candles)
    if (rsiData.length > 0) {
      const rsiSeries = chart.addSeries(LineSeries, {
        color: '#a78bfa',
        lineWidth: 1,
        priceScaleId: 'rsi',
        autoscaleInfoProvider: () => ({
          priceRange: { minValue: 0, maxValue: 100 },
          margins: { above: 0.1, below: 0.1 },
        }),
      }, 2)
      rsiSeries.setData(rsiData)

      rsiSeries.createPriceLine({
        price: 70, color: '#ef444466', lineWidth: 1,
        lineStyle: LineStyle.Dashed, title: '70', axisLabelVisible: true,
      })
      rsiSeries.createPriceLine({
        price: 30, color: '#22c55e66', lineWidth: 1,
        lineStyle: LineStyle.Dashed, title: '30', axisLabelVisible: true,
      })
    }

    // ── Pane 0: 7-day predicted price path ───────────────────────────────────
    if (prediction7d?.dayByDay?.length) {
      const lastCandle = analysis.candles[analysis.candles.length - 1]
      const anchor = { time: lastCandle.timestamp as UTCTimestamp, value: lastCandle.close }

      const predLine = chart.addSeries(LineSeries, {
        color: '#818cf8',
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        priceScaleId: 'right',
        lastValueVisible: true,
        title: '7d',
      }, 0)
      predLine.setData([anchor, ...prediction7d.dayByDay.map(d => ({
        time: d.timestamp as UTCTimestamp,
        value: d.predictedClose,
      }))])

      const highLine = chart.addSeries(LineSeries, {
        color: '#818cf844',
        lineWidth: 1,
        lineStyle: LineStyle.Dotted,
        priceScaleId: 'right',
        lastValueVisible: false,
      }, 0)
      highLine.setData([anchor, ...prediction7d.dayByDay.map(d => ({
        time: d.timestamp as UTCTimestamp,
        value: d.predictedHigh,
      }))])

      const lowLine = chart.addSeries(LineSeries, {
        color: '#818cf844',
        lineWidth: 1,
        lineStyle: LineStyle.Dotted,
        priceScaleId: 'right',
        lastValueVisible: false,
      }, 0)
      lowLine.setData([anchor, ...prediction7d.dayByDay.map(d => ({
        time: d.timestamp as UTCTimestamp,
        value: d.predictedLow,
      }))])
    }

    // Distribute pane heights
    const panes = chart.panes()
    if (panes[0]) panes[0].setStretchFactor(5)
    if (panes[1]) panes[1].setStretchFactor(1)
    if (panes[2]) panes[2].setStretchFactor(1.5)

    chart.timeScale().fitContent()

    const ro = new ResizeObserver(() => chart.applyOptions({ width: el.clientWidth }))
    ro.observe(el)

    return () => { ro.disconnect(); chart.remove() }
  }, [analysis, prediction7d])

  return <div ref={containerRef} className="w-full" />
}

// ─── news list ────────────────────────────────────────────────────────────────

function NewsList({ items }: { items: NewsItem[] }) {
  if (!items.length) return null
  return (
    <div className="bg-surface-card border border-white/5 rounded-xl p-4">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-muted mb-3">Latest News</p>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="border-b border-white/5 pb-2 last:border-0 last:pb-0">
            <a href={item.url} target="_blank" rel="noopener noreferrer"
               className="text-sm text-ink hover:text-blue-400 leading-snug block">
              {item.title}
            </a>
            <p className="text-[11px] text-ink-muted mt-0.5">
              {item.source}{item.publishedAt ? ` · ${item.publishedAt}` : ''}
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ─── Monte Carlo band chart (SVG) ────────────────────────────────────────────

function MonteCarloChart({ days, anchorPrice }: { days: DayPrediction[]; anchorPrice: number }) {
  const mcDays = days.filter(d => d.mcP50 != null)
  if (!mcDays.length) return null

  const W = 600, H = 160, padL = 52, padB = 20

  const chartW = W - padL
  const chartH = H - padB

  let minP = anchorPrice, maxP = anchorPrice
  for (const d of mcDays) {
    minP = Math.min(minP, d.mcP10!)
    maxP = Math.max(maxP, d.mcP90!)
  }
  const range = maxP - minP
  if (range <= 0) return null
  const pad = range * 0.08
  const lo = minP - pad, hi = maxP + pad

  const toX = (i: number) => padL + (i / mcDays.length) * chartW
  const toY = (p: number) => chartH - ((p - lo) / (hi - lo)) * chartH

  const p10 = [{ x: toX(0), y: toY(anchorPrice) }, ...mcDays.map((d, i) => ({ x: toX(i + 1), y: toY(d.mcP10!) }))]
  const p50 = [{ x: toX(0), y: toY(anchorPrice) }, ...mcDays.map((d, i) => ({ x: toX(i + 1), y: toY(d.mcP50!) }))]
  const p90 = [{ x: toX(0), y: toY(anchorPrice) }, ...mcDays.map((d, i) => ({ x: toX(i + 1), y: toY(d.mcP90!) }))]

  const pts = (arr: { x: number; y: number }[]) => arr.map(p => `${p.x},${p.y}`).join(' ')
  const bandPts = [...p10, ...[...p90].reverse()].map(p => `${p.x},${p.y}`).join(' ')
  const polyline = (arr: { x: number; y: number }[]) => arr.map(p => `${p.x},${p.y}`).join(' ')

  const gridPrices = [lo + pad, (lo + hi) / 2, hi - pad]
  const fmt = (v: number) => v >= 10000 ? v.toFixed(0) : v >= 100 ? v.toFixed(1) : v.toFixed(2)

  return (
    <div className="rounded-lg overflow-hidden border border-white/5">
      {/* legend */}
      <div className="flex items-center gap-3 px-3 py-1.5 bg-[#0F1424] text-[10px] text-[#8B8FA3]">
        <span className="inline-block w-5 h-2.5 rounded-sm bg-violet-500/25" />
        P10–P90 band
        <span className="inline-block w-5 border-t-2 border-violet-400" />
        P50 median
        <span className="ml-auto italic">Monte Carlo · GBM · 10K paths</span>
      </div>
      {/* chart */}
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ background: '#0F1424', display: 'block' }}>
        {/* grid */}
        {[1, 2, 3].map(i => (
          <line key={i} x1={padL} y1={chartH * i / 4} x2={W} y2={chartH * i / 4} stroke="#1F2747" strokeWidth="0.5" />
        ))}
        {/* band fill */}
        <polygon points={bandPts} fill="rgba(139,92,246,0.15)" />
        {/* P10 / P90 borders */}
        <polyline points={pts(p10)} fill="none" stroke="rgba(139,92,246,0.4)" strokeWidth="1" />
        <polyline points={pts(p90)} fill="none" stroke="rgba(139,92,246,0.4)" strokeWidth="1" />
        {/* P50 median */}
        <polyline points={polyline(p50)} fill="none" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {/* anchor dot */}
        <circle cx={p50[0].x} cy={p50[0].y} r="3.5" fill="white" />
        {/* P50 dots per day */}
        {p50.slice(1).map((pt, i) => <circle key={i} cx={pt.x} cy={pt.y} r="2.5" fill="#8B5CF6" />)}
        {/* Y labels */}
        {gridPrices.map((price, i) => (
          <text key={i} x="2" y={toY(price) + 4} fontSize="9" fill="#8B8FA3" fontFamily="monospace">${fmt(price)}</text>
        ))}
        {/* X labels */}
        <text x={toX(0) - 10} y={H - 4} fontSize="9" fill="#8B8FA3" fontFamily="monospace">Today</text>
        {mcDays.map((_, i) => (
          <text key={i} x={toX(i + 1) - 6} y={H - 4} fontSize="9" fill="#8B8FA3" fontFamily="monospace">D{i + 1}</text>
        ))}
      </svg>
    </div>
  )
}

// ─── 7-day prediction panel ───────────────────────────────────────────────────

function Prediction7dPanel({ pred }: { pred: Prediction7d }) {
  const { summary, dayByDay } = pred
  const changeColor = (pct: number) => pct >= 0 ? 'text-emerald-400' : 'text-red-400'
  const sign = (pct: number) => (pct >= 0 ? '+' : '') + pct.toFixed(2) + '%'

  return (
    <div className="bg-surface-card border border-white/5 rounded-xl p-4">
      <div className="bg-indigo-500/10 rounded-lg px-3 py-2 mb-3 flex flex-wrap items-center gap-4">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-indigo-400">
          7-Day Pattern Forecast
        </p>
        <span className="text-xs text-ink-muted">
          {summary.templateCount} template{summary.templateCount !== 1 ? 's' : ''} matched
          &nbsp;·&nbsp;pattern: <span className="text-ink-soft">{summary.dominantPattern}</span>
        </span>
        <span className="ml-auto text-xs text-ink-muted">
          confidence&nbsp;
          <span className="font-semibold text-ink">{(summary.confidence * 100).toFixed(0)}%</span>
        </span>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-green-900/20 border border-green-900/40 rounded-lg px-3 py-2 text-center">
          <p className="text-[10px] text-ink-muted uppercase mb-0.5">Bull</p>
          <p className="text-sm font-bold text-emerald-400">{(summary.bullProbability * 100).toFixed(0)}%</p>
        </div>
        <div className="bg-surface-elevated border border-white/10 rounded-lg px-3 py-2 text-center">
          <p className="text-[10px] text-ink-muted uppercase mb-0.5">Base</p>
          <p className="text-sm font-bold text-ink">{(summary.baseProbability * 100).toFixed(0)}%</p>
        </div>
        <div className="bg-red-900/20 border border-red-900/40 rounded-lg px-3 py-2 text-center">
          <p className="text-[10px] text-ink-muted uppercase mb-0.5">Bear</p>
          <p className="text-sm font-bold text-red-400">{(summary.bearProbability * 100).toFixed(0)}%</p>
        </div>
      </div>

      {/* Monte Carlo band chart */}
      {dayByDay.some(d => d.mcP50 != null) && (
        <div className="mb-3">
          <MonteCarloChart days={dayByDay} anchorPrice={pred.anchorPrice} />
        </div>
      )}

      {/* Day-by-day table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="text-ink-muted border-b border-white/5">
              <th className="text-left py-1.5 pr-3 font-medium">Day</th>
              <th className="text-left py-1.5 pr-3 font-medium">Date</th>
              <th className="text-right py-1.5 pr-3 font-medium">Low</th>
              <th className="text-right py-1.5 pr-3 font-medium">Close</th>
              <th className="text-right py-1.5 pr-3 font-medium">High</th>
              <th className="text-right py-1.5 pr-3 font-medium">Change</th>
              <th className="text-right py-1.5 font-medium">Conf.</th>
            </tr>
          </thead>
          <tbody>
            {dayByDay.map((d: DayPrediction) => (
              <tr key={d.day} className="border-b border-white/5 last:border-0 hover:bg-surface-elevated/60">
                <td className="py-1.5 pr-3 text-ink-muted">D{d.day}</td>
                <td className="py-1.5 pr-3 text-ink-soft">{d.date}</td>
                <td className="py-1.5 pr-3 text-right text-red-300">${d.predictedLow.toFixed(2)}</td>
                <td className={`py-1.5 pr-3 text-right font-semibold ${changeColor(d.changePct)}`}>
                  ${d.predictedClose.toFixed(2)}
                </td>
                <td className="py-1.5 pr-3 text-right text-green-300">${d.predictedHigh.toFixed(2)}</td>
                <td className={`py-1.5 pr-3 text-right font-semibold ${changeColor(d.changePct)}`}>
                  {sign(d.changePct)}
                </td>
                <td className="py-1.5 text-right text-ink-muted">
                  {(d.confidence * 100).toFixed(0)}%
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-white/5">
              <td colSpan={3} className="pt-2 text-ink-muted">7-day target</td>
              <td className={`pt-2 text-right font-bold ${changeColor(summary.sevenDayChangePct)}`}>
                ${summary.priceTarget.toFixed(2)}
              </td>
              <td />
              <td className={`pt-2 text-right font-bold ${changeColor(summary.sevenDayChangePct)}`}>
                {sign(summary.sevenDayChangePct)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

// ─── report view ─────────────────────────────────────────────────────────────

function ReportView({ report, asset }: { report: MarketReportResponse; asset: Asset }) {
  const bull = report.analysis.bullish
  return (
    <div className="space-y-4">
      {/* Trend badge */}
      <div className="flex items-center gap-3">
        <span className={`text-xs font-bold px-3 py-1 rounded-full ${
          bull ? 'bg-green-900/60 text-emerald-400' : 'bg-red-900/60 text-red-400'
        }`}>
          {bull ? 'BULLISH ▲' : 'BEARISH ▼'}
        </span>
        <span className="text-xs text-ink-muted">
          {asset} · {new Date(report.analysis.computedAt).toLocaleString()}
        </span>
      </div>

      {/* Candlestick chart — full width, no sidebar */}
      <div className="bg-surface-card border border-white/5 rounded-xl overflow-hidden">
        <CandlestickChart analysis={report.analysis} prediction7d={report.prediction7d} />
      </div>

      {/* AI prediction */}
      <div className="bg-surface-card border border-indigo-900/60 rounded-xl p-4">
        <div className="bg-indigo-500/10 rounded-lg px-3 py-2 mb-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-indigo-400">
            AI Market Outlook
          </p>
        </div>
        <div className="text-indigo-200 text-sm leading-relaxed prose prose-sm prose-invert max-w-none
          [&_h1]:text-indigo-300 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-3 [&_h1]:mb-1
          [&_h2]:text-indigo-300 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1
          [&_h3]:text-indigo-400 [&_h3]:text-xs [&_h3]:font-semibold [&_h3]:uppercase [&_h3]:tracking-wide [&_h3]:mt-2 [&_h3]:mb-1
          [&_strong]:text-indigo-100 [&_em]:italic
          [&_ul]:mt-1 [&_ul]:mb-1 [&_ul]:list-disc [&_ul]:list-inside
          [&_ol]:mt-1 [&_ol]:mb-1 [&_ol]:list-decimal [&_ol]:list-inside
          [&_li]:ml-2 [&_li]:my-0.5
          [&_p]:mt-2 [&_p]:first:mt-0
          [&_code]:bg-indigo-950/60 [&_code]:text-indigo-300 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs
          [&_pre]:bg-indigo-950/60 [&_pre]:rounded-lg [&_pre]:p-3 [&_pre]:overflow-x-auto
          [&_blockquote]:border-l-2 [&_blockquote]:border-indigo-500/50 [&_blockquote]:pl-3 [&_blockquote]:text-indigo-300/70
          [&_hr]:border-indigo-900/60 [&_hr]:my-3
          [&_table]:w-full [&_table]:text-xs [&_th]:text-indigo-400 [&_th]:font-semibold [&_th]:border-b [&_th]:border-indigo-900/60 [&_th]:pb-1 [&_td]:border-b [&_td]:border-indigo-900/30 [&_td]:py-1">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {report.aiPrediction}
          </ReactMarkdown>
        </div>
      </div>

      {report.prediction7d && <Prediction7dPanel pred={report.prediction7d} />}

      <NewsList items={report.newsItems} />
    </div>
  )
}

// ─── send status feedback ─────────────────────────────────────────────────────

type SendStatus = { kind: 'sent'; count: number } | { kind: 'failed' } | null

// ─── main panel ──────────────────────────────────────────────────────────────

export function MarketReportPanel() {
  const [asset,   setAsset]   = useState<Asset>('SOL')
  const [report,  setReport]  = useState<MarketReportResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState<string | null>(null)

  const [profile,     setProfile]     = useState<UserProfile | null>(null)
  const [sendStatus,  setSendStatus]  = useState<SendStatus>(null)
  const [sendingTg,   setSendingTg]   = useState(false)
  const [sendingEmail,setSendingEmail]= useState(false)

  const { startTracking, latestByAsset, pending } = usePendingReports()
  const { portfolioId } = useAuth()

  // Load user profile on mount for channel availability checks
  useEffect(() => {
    if (!portfolioId) return
    fetchProfile(portfolioId)
      .then(p => setProfile(p))
      .catch(() => setProfile(null))
  }, [portfolioId])

  // Pick up the latest report from the app-level context — keeps this view in sync when the
  // user leaves the page during background generation and returns.
  useEffect(() => {
    const latest = latestByAsset[asset]
    if (latest && !latest.pending) setReport(latest)
  }, [asset, latestByAsset])

  const isPending = pending.has(asset) || (report?.pending ?? false)

  const hasTelegram = !!(profile?.hasTelegramToken && profile?.telegramChatId)
  const hasEmail    = !!(profile?.notificationEmail)

  const showSendStatus = (status: SendStatus) => {
    setSendStatus(status)
    setTimeout(() => setSendStatus(null), 3000)
  }

  const handleSend = async (channel: 'telegram' | 'email') => {
    if (!portfolioId) return
    const setSending = channel === 'telegram' ? setSendingTg : setSendingEmail
    setSending(true)
    try {
      const result = await sendMarketReport(asset)
      showSendStatus({ kind: 'sent', count: result.dispatched })
    } catch {
      showSendStatus({ kind: 'failed' })
    } finally {
      setSending(false)
    }
  }

  const handleGenerate = () => {
    setLoading(true)
    setError(null)
    generateMarketReport(asset)
      .then(r => {
        setReport(r)
        if (r.pending) startTracking(asset)
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to generate report'))
      .finally(() => setLoading(false))
  }

  const hasReport = !!(report && !report.pending)
  const canSendTelegram = hasReport && hasTelegram
  const canSendEmail    = hasReport && hasEmail

  return (
    <section className="space-y-4">
      <h2 className="text-sm font-semibold uppercase tracking-widest text-ink-muted">
        Market Research
      </h2>

      <div className="bg-surface-card rounded-xl border border-white/5 overflow-hidden">

        {/* toolbar */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-white/5">
          <div className="flex items-center gap-1">
            {ASSETS.map(a => (
              <button
                key={a}
                onClick={() => { setAsset(a); setReport(null); setError(null) }}
                className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${
                  asset === a
                    ? 'bg-accent-blue text-ink'
                    : 'bg-surface-elevated text-ink-muted hover:bg-surface-elevated/80'
                }`}
              >
                {a}
              </button>
            ))}
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {report && !report.pending && report.generatedAt && (
              <span className="text-xs text-ink-muted">
                {report.cached ? 'Cached' : 'Fresh'} · {relativeTime(report.generatedAt)}
              </span>
            )}
            {isPending && (
              <span className="text-xs text-indigo-400 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin" />
                Generating…
              </span>
            )}

            {/* Send buttons — always visible; disabled until a report is ready / channel is configured */}
            <button
              onClick={() => canSendTelegram ? handleSend('telegram') : undefined}
              disabled={sendingTg || !canSendTelegram}
              title={!hasReport ? 'Generate a report first' : !hasTelegram ? 'Configure Telegram in Settings' : undefined}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-semibold transition-all duration-200 ${
                canSendTelegram
                  ? 'border-indigo-500/40 bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20 disabled:opacity-50 cursor-pointer'
                  : 'border-white/10 bg-surface-elevated text-ink-muted/50 cursor-not-allowed'
              }`}
            >
              {sendingTg ? <Spinner size="sm" /> : '✈'}
              Telegram
            </button>
            <button
              onClick={() => canSendEmail ? handleSend('email') : undefined}
              disabled={sendingEmail || !canSendEmail}
              title={!hasReport ? 'Generate a report first' : !hasEmail ? 'Configure email in Settings' : undefined}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-semibold transition-all duration-200 ${
                canSendEmail
                  ? 'border-blue-500/40 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 disabled:opacity-50 cursor-pointer'
                  : 'border-white/10 bg-surface-elevated text-ink-muted/50 cursor-not-allowed'
              }`}
            >
              {sendingEmail ? <Spinner size="sm" /> : '✉'}
              Email
            </button>

            <button
              onClick={handleGenerate}
              disabled={loading || isPending}
              className="flex items-center gap-2 px-4 py-1.5 rounded bg-accent-blue text-ink text-xs font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {loading ? <Spinner size="sm" /> : '▶'}
              Generate Report
            </button>
          </div>
        </div>

        {/* Send status feedback */}
        {sendStatus && (
          <div className={`px-4 py-2 text-xs flex items-center gap-1.5 border-b ${
            sendStatus.kind === 'sent'
              ? 'text-emerald-400 border-emerald-500/20 bg-emerald-500/5'
              : 'text-accent-red border-red-500/20 bg-red-500/5'
          }`}>
            {sendStatus.kind === 'sent'
              ? `Sent to ${sendStatus.count} channel${sendStatus.count !== 1 ? 's' : ''} ✓`
              : 'Failed to send ✗'}
          </div>
        )}

        {/* content */}
        <div className="p-4">
          {loading && !report && (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <Spinner size="lg" />
              <span className="text-ink-muted text-sm">
                Fetching market data…
              </span>
            </div>
          )}

          {error && !loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <span className="text-accent-red text-sm text-center">{error}</span>
              <button onClick={handleGenerate} className="text-xs text-ink-muted hover:text-ink underline">
                Retry
              </button>
            </div>
          )}

          {!report && !loading && !error && !isPending && (
            <div className="flex items-center justify-center py-20">
              <span className="text-ink-muted text-sm">
                Select an asset and click Generate Report for a full daily analysis with AI predictions and news.
              </span>
            </div>
          )}

          {!report && !loading && !error && isPending && (
            <div className="bg-indigo-500/5 border border-indigo-500/30 rounded-xl px-4 py-3 flex items-center gap-3">
              <div className="w-4 h-4 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-indigo-200">Generating {asset} report in background…</div>
                <div className="text-[11px] text-ink-muted">
                  Feel free to navigate away — a notification will appear when it's ready.
                </div>
              </div>
            </div>
          )}

          {report && report.pending && !loading && (
            <div className="bg-indigo-500/5 border border-indigo-500/30 rounded-xl px-4 py-3 flex items-center gap-3">
              <div className="w-4 h-4 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-indigo-200">Generating {asset} report…</div>
                <div className="text-[11px] text-ink-muted">
                  Fetching news and running AI analysis. You can switch to other pages — you'll get a notification when it's ready.
                </div>
              </div>
            </div>
          )}

          {report && !report.pending && !loading && (
            <ReportView report={report} asset={asset} />
          )}
        </div>
      </div>
    </section>
  )
}
